import { X } from 'lucide-react';
import React from 'react';
import {
	Dialog as RACDialog,
	type DialogProps,
	Heading as RACHeading,
	Modal as RACModal,
	ModalOverlay as RACModalOverlay,
	type ModalOverlayProps,
	composeRenderProps
} from 'react-aria-components';
import { cn } from './utils';

type DrawerSide = 'left' | 'right';
type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'half' | 'full';
const sizeStyles: Record<DrawerSize, string> = {
	sm: 'w-full max-w-sm',
	md: 'w-full max-w-md',
	lg: 'w-full max-w-lg',
	xl: 'w-full max-w-2xl',
	half: 'w-1/2',
	full: 'w-full'
};
function drawerOverlayStyles(renderProps: { isEntering?: boolean; isExiting?: boolean }) {
	return cn(
		'fixed inset-0 z-20 bg-black/[50%] backdrop-blur-lg',
		renderProps.isEntering && 'animate-in fade-in duration-200 ease-out',
		renderProps.isExiting && 'animate-out fade-out duration-200 ease-in'
	);
}
function drawerStyles(renderProps: { isEntering?: boolean; isExiting?: boolean }, side: DrawerSide, size: DrawerSize) {
	return cn(
		'fixed top-0 bottom-0 flex h-full flex-col border-black/10 bg-white bg-clip-padding text-left align-middle font-sans text-slate-700 shadow-2xl forced-colors:bg-[Canvas]',
		sizeStyles[size],
		side === 'right' && 'right-0 border-l',
		side === 'left' && 'left-0 border-r',
		renderProps.isEntering && 'duration-300 ease-out',
		renderProps.isExiting && 'duration-200 ease-in',
		side === 'right' && renderProps.isEntering && 'animate-in slide-in-from-right',
		side === 'right' && renderProps.isExiting && 'animate-out slide-out-to-right',
		side === 'left' && renderProps.isEntering && 'animate-in slide-in-from-left',
		side === 'left' && renderProps.isExiting && 'animate-out slide-out-to-left'
	);
}
interface DrawerHeaderProps {
	closeDrawer?: () => void;
	title: string | React.ReactNode;
	status?: string;
}
function DrawerHeader({ closeDrawer, title, status }: DrawerHeaderProps) {
	return (
		<div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
			<div className="flex flex-col">
				<RACHeading slot="title" className="text-base font-semibold text-gray-900">
					{title}
				</RACHeading>
				{status && <p className="mt-0.5 text-xs text-gray-500">{status}</p>}
			</div>
			{closeDrawer && (
				<button
					type="button"
					onClick={closeDrawer}
					className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
					aria-label="Close drawer"
				>
					<X size={18} />
				</button>
			)}
		</div>
	);
}
export interface DrawerProps extends Omit<ModalOverlayProps, 'children'>, Omit<DrawerHeaderProps, 'title'> {
	children: React.ReactNode;
	title?: string | React.ReactNode;
	footer?: React.ReactNode;
	side?: DrawerSide;
	size?: DrawerSize;
	dialogProps?: Omit<DialogProps, 'children'>;
	'aria-label'?: string;
}
export function Drawer({
	children,
	title,
	footer,
	side = 'right',
	size = 'md',
	closeDrawer,
	status,
	dialogProps,
	isDismissable = true,
	'aria-label': ariaLabel,
	...props
}: DrawerProps) {
	return (
		<RACModalOverlay
			{...props}
			isDismissable={isDismissable}
			className={composeRenderProps(props.className, (className, renderProps) => cn(drawerOverlayStyles(renderProps), className))}
		>
			<RACModal className={renderProps => drawerStyles(renderProps, side, size)}>
				<RACDialog
					{...dialogProps}
					aria-label={!title ? ariaLabel ?? 'Drawer' : undefined}
					className={cn('flex h-full flex-col outline outline-0', dialogProps?.className)}
				>
					{title && <DrawerHeader title={title} closeDrawer={closeDrawer} status={status} />}
					<div className="flex-1 overflow-y-auto">{children}</div>
					{footer && (
						<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 border-t bg-white">
							<div className="p-4">{footer}</div>
						</div>
					)}
				</RACDialog>
			</RACModal>
		</RACModalOverlay>
	);
}
